import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import { buttonReset, focusRing } from '../../../../shared/theme/focusStyles';
import { WEEKDAYS } from './copyDateUtils';

const WEEKEND_KEYS = ['saturday', 'sunday'];

const SHORTCUTS = [
  { id: 'weekdays', label: 'Weekdays', isDaySelected: (key) => !WEEKEND_KEYS.includes(key) },
  { id: 'every-day', label: 'Every day', isDaySelected: () => true },
];

const buildDays = (isDaySelected) => WEEKDAYS.reduce((days, day) => ({
  ...days,
  [day.key]: isDaySelected(day.key),
}), {});

const SelectorContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const DayButtonsRow = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  @media (max-width: 480px) {
    gap: 6px;
  }
`;

const DayButton = styled('button')`
  ${buttonReset}
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid ${({ selected, theme }) => (selected ? theme.colors.buttonPrimaryBg : theme.colors.borderMedium)};
  border-radius: 50%;
  background-color: ${({ selected, theme }) => (selected ? theme.colors.buttonPrimaryBg : theme.colors.surface)};
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: ${({ selected, theme }) => (selected ? theme.colors.white : theme.colors.text)};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.text};
  }

  @media (max-width: 480px) {
    width: 36px;
    height: 36px;
    font-size: 13px;
  }
  ${focusRing}
`;

const ShortcutsRow = styled('div')`
  display: flex;
  gap: 8px;
`;

const ShortcutButton = styled('button')`
  ${buttonReset}
  padding: 6px 12px;
  border: 1px solid ${({ theme }) => theme.colors.borderMedium};
  border-radius: 14px;
  background: ${({ pressed, theme }) => (pressed ? theme.colors.surfaceSecondary : theme.colors.surface)};
  font-family: 'Nunito Sans';
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.text};
  }
  ${focusRing}
`;

const CopyDaySelector = ({
  selectedDays, onToggleDay, onSetDays, showShortcuts,
}) => {
  const handleDayClick = (e) => {
    onToggleDay(e.currentTarget.value);
  };

  const handleShortcutClick = (e) => {
    const shortcut = SHORTCUTS.find((option) => option.id === e.currentTarget.value);
    onSetDays(buildDays(shortcut.isDaySelected));
  };

  const isShortcutActive = (shortcut) => WEEKDAYS
    .every((day) => !!selectedDays[day.key] === shortcut.isDaySelected(day.key));

  return (
    <SelectorContainer>
      <DayButtonsRow aria-label='Days of the week' role='group'>
        {WEEKDAYS.map((day) => (
          <DayButton
            aria-label={day.label}
            aria-pressed={!!selectedDays[day.key]}
            key={day.key}
            onClick={handleDayClick}
            selected={!!selectedDays[day.key]}
            type='button'
            value={day.key}
          >
            {day.short}
          </DayButton>
        ))}
      </DayButtonsRow>
      {showShortcuts && (
        <ShortcutsRow>
          {SHORTCUTS.map((shortcut) => (
            <ShortcutButton
              aria-pressed={isShortcutActive(shortcut)}
              key={shortcut.id}
              onClick={handleShortcutClick}
              pressed={isShortcutActive(shortcut)}
              type='button'
              value={shortcut.id}
            >
              {shortcut.label}
            </ShortcutButton>
          ))}
        </ShortcutsRow>
      )}
    </SelectorContainer>
  );
};

CopyDaySelector.propTypes = {
  selectedDays: PropTypes.objectOf(PropTypes.bool).isRequired,
  onToggleDay: PropTypes.func.isRequired,
  onSetDays: PropTypes.func.isRequired,
  showShortcuts: PropTypes.bool,
};

CopyDaySelector.defaultProps = {
  showShortcuts: true,
};

export default CopyDaySelector;
